import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Pagination,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import ReactPlayer from "react-player";
import TrainingIcon from "@icons/TrainingIcon";
import {
  getTrainingVideos,
  getTrainingVideoPlaybackUrl,
  resolveTrainingVideoUrl,
  TrainingVideo,
} from "@services/trainingVideos";
import { getApiOrigin } from "@utils/apiOrigin";

const PAGE_SIZE = 12;
const API_BASE_URL = getApiOrigin();
const HIDDEN_CATEGORY_KEYWORD = "hide";

/** Same title band as Profile / Regulations Library (Discovery 80px + 20px). */
const PAGE_TITLE_BAND_HEIGHT_PX = 100;

const PageContainer = styled(Box)({
  backgroundColor: "#fdffff",
  borderRadius: "12px",
  minHeight: "100%",
  height: "auto",
  flex: "1 0 auto",
  display: "flex",
  flexDirection: "column",
  overflow: "visible",
  position: "relative",
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
});

const Header = styled(Box)({
  backgroundColor: "rgba(253, 255, 255, 0.2)",
  borderBottom: "1px solid #35d7e4",
  flexShrink: 0,
  boxSizing: "border-box",
});

const TitleBand = styled(Box)(({ theme }) => ({
  height: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
  padding: "0 64px 24px",
  display: "flex",
  alignItems: "flex-end",
  boxSizing: "border-box",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: "0 16px 16px",
    height: "auto",
    minHeight: `${PAGE_TITLE_BAND_HEIGHT_PX}px`,
    alignItems: "flex-end",
  },
}));

/** Icon sits only beside the Learning Center title, not the subtitle. */
const HeaderTitleRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const HeaderIconWrap = styled(Box)({
  width: "28px",
  height: "28px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& svg": {
    width: "28px",
    height: "28px",
    display: "block",
  },
});

const HeaderTitle = styled(Typography)({
  fontSize: "28px",
  fontWeight: 500,
  color: "#262626",
  fontFamily: "Rethink Sans, sans-serif",
  lineHeight: 1.5,
  letterSpacing: "-1px",
  margin: 0,
});

const HeaderSubtitle = styled(Typography)({
  fontSize: "14px",
  color: "#4b5563",
  fontFamily: "Rethink Sans, sans-serif",
  margin: 0,
  lineHeight: 1.5,
});

/** Pull subtitle up into the title band’s bottom padding (24px − 4px gap). */
const HeaderSubtitleRow = styled(Box)(({ theme }) => ({
  padding: "0 64px 20px",
  marginTop: "-20px",
  [theme.breakpoints.down("md")]: {
    padding: "0 16px 16px",
    marginTop: "-12px",
  },
}));

const Content = styled(Box)(({ theme }) => ({
  flex: "1 0 auto",
  overflow: "visible",
  padding: "16px 48px 24px",
  display: "flex",
  flexDirection: "column",
  gap: "20px",
  boxSizing: "border-box",
  [theme.breakpoints.down("md")]: {
    padding: "12px 16px 24px",
  },
}));

const FilterRow = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "minmax(240px, 1fr) 220px 220px",
  gap: "16px",
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const CardsGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: "16px",
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const VideoCard = styled(Card)({
  borderRadius: "12px",
  border: "1px solid #c8eef5",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fdfe 100%)",
  boxShadow: "0px 4px 14px rgba(6, 91, 112, 0.08)",
});

const VideoTitle = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  color: "#1f2937",
});

const VideoDescription = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "14px",
  lineHeight: 1.6,
  color: "#4b5563",
  minHeight: "66px",
});

const MetaText = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  color: "#6b7280",
});

const EmptyState = styled(Box)({
  border: "1px dashed #99d4e1",
  borderRadius: "12px",
  padding: "36px 20px",
  textAlign: "center",
  backgroundColor: "#f8fdfe",
});

const CategorySection = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  border: "1px solid #c8eef5",
  borderRadius: "16px",
  background: "linear-gradient(180deg, #f5fcfe 0%, #ffffff 100%)",
  boxShadow: "0px 4px 14px rgba(6, 91, 112, 0.06)",
  padding: "18px",
});

const CategorySectionTitle = styled(Typography)({
  fontSize: "18px",
  fontWeight: 600,
  color: "#065b70",
  fontFamily: "Rethink Sans, sans-serif",
  padding: "10px 14px",
  border: "1px solid #99d4e1",
  borderRadius: "12px",
  backgroundColor: "#e6fafc",
  boxShadow: "inset 0px 1px 0px rgba(255,255,255,0.65)",
});

function isServerHostedVideo(value?: string | null): boolean {
  const resolved = resolveTrainingVideoUrl(value);
  if (!resolved || !API_BASE_URL) return false;

  try {
    return new URL(resolved).origin === new URL(API_BASE_URL).origin;
  } catch {
    return false;
  }
}

function formatDate(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function getVideoDateMeta(video: TrainingVideo): string | null {
  const created = formatDate((video as any).created_at);
  const updated = formatDate((video as any).updated_at);

  if (!created && !updated) {
    return null;
  }

  if (created && updated && created !== updated) {
    return `Updated on: ${updated}`;
  }

  // If dates are the same or only one is available, show Created
  return `Created on: ${created || updated}`;
}

function getSearchableText(video: TrainingVideo): string {
  return `${video.name || ""} ${video.description || ""} ${video.category || ""}`.toLowerCase();
}

function isHiddenCategory(category?: string | null): boolean {
  return (category || "").trim().toLowerCase().includes(HIDDEN_CATEGORY_KEYWORD);
}

function isVisibleTrainingVideo(video: TrainingVideo): boolean {
  return video.is_active && !video.is_delete && !isHiddenCategory(video.category);
}

function getMediaExtension(value?: string | null): string {
  const source = (value || "").split(/[?#]/)[0].trim().toLowerCase();
  const match = source.match(/\.([a-z0-9]+)$/i);
  return match?.[1] || "";
}

function shouldUseNativeAudioPlayer(value?: string | null): boolean {
  const extension = getMediaExtension(value);
  return ["mp3", "wav", "m4a", "aac", "flac"].includes(extension);
}

function shouldUseNativeVideoPlayer(value?: string | null): boolean {
  if (!value) return false;
  if (shouldUseNativeAudioPlayer(value)) return false;
  if (value.startsWith("blob:")) return true;

  return ["mp4", "webm", "ogg", "ogv", "mov", "m4v", "m3u8"].includes(
    getMediaExtension(value),
  );
}

const LoadingCards: React.FC = () => (
  <CardsGrid>
    {Array.from({ length: 6 }).map((_, index) => (
      <VideoCard key={index}>
        <CardContent sx={{ p: 2.5 }}>
          <Skeleton variant="rounded" width={100} height={28} />
          <Skeleton sx={{ mt: 2 }} variant="text" width="80%" height={32} />
          <Skeleton variant="text" width="100%" />
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="text" width="70%" />
          <Skeleton sx={{ mt: 2 }} variant="rounded" width={120} height={36} />
        </CardContent>
      </VideoCard>
    ))}
  </CardsGrid>
);

const TrainingVideosPage: React.FC = () => {
  const [videos, setVideos] = useState<TrainingVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("all");
  const [displayFormat, setDisplayFormat] = useState("grid");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [activeVideo, setActiveVideo] = useState<TrainingVideo | null>(null);
  const [activeVideoUrl, setActiveVideoUrl] = useState("");
  const [videoLoadingId, setVideoLoadingId] = useState<number | null>(null);
  const [videoPlayerError, setVideoPlayerError] = useState<string | null>(null);

  const fetchAllVisibleVideos = async (selectedCategory: string) => {
    const pageSize = 100;
    let currentPage = 1;
    let totalItems = 0;
    const collected: TrainingVideo[] = [];

    do {
      const result = await getTrainingVideos({
        category: selectedCategory === "all" ? undefined : selectedCategory,
        is_active: true,
        page: currentPage,
        page_size: pageSize,
      });

      collected.push(
        ...result.items.filter((video) => isVisibleTrainingVideo(video)),
      );
      totalItems = result.total;
      currentPage += 1;

      if (result.items.length === 0) {
        break;
      }
    } while (collected.length < totalItems);

    return collected;
  };

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let nextVideos: TrainingVideo[] = [];

      if (displayFormat === "categorywise") {
        const allVideos = await fetchAllVisibleVideos(category);
        nextVideos = allVideos;
        setVideos(allVideos);
        setTotal(allVideos.length);
      } else {
        const result = await getTrainingVideos({
          category: category === "all" ? undefined : category,
          is_active: true,
          page,
          page_size: PAGE_SIZE,
        });

        const visibleVideos = result.items.filter(
          (video) => isVisibleTrainingVideo(video),
        );

        nextVideos = visibleVideos;
        setVideos(visibleVideos);
        setTotal(result.total);
      }

      if (category === "all") {
        const categorySourceVideos =
          displayFormat === "categorywise"
            ? nextVideos
            : await fetchAllVisibleVideos("all");

        const categories = Array.from(
          new Set(
            categorySourceVideos
              .map((item) => (item.category || "").trim())
              .filter((value) => Boolean(value) && !isHiddenCategory(value)),
          ),
        ).sort((a, b) => a.localeCompare(b));

        setAvailableCategories(categories);
      }
    } catch (err: any) {
      setError(err?.message || "Unable to load training videos");
      setVideos([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [category, page, displayFormat]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  useEffect(() => {
    setPage(1);
  }, [category]);

  const filteredVideos = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const visibleVideos = videos.filter((video) => !isHiddenCategory(video.category));
    if (!term) return visibleVideos;

    return visibleVideos.filter((video) => getSearchableText(video).includes(term));
  }, [videos, searchTerm]);

  const groupedVideos = useMemo(() => {
    const groups = filteredVideos.reduce<Record<string, TrainingVideo[]>>(
      (acc, video) => {
        const groupName = (video.category || "").trim() || "Uncategorized";
        if (!acc[groupName]) {
          acc[groupName] = [];
        }
        acc[groupName].push(video);
        return acc;
      },
      {},
    );

    return Object.entries(groups).sort(([a], [b]) => {
      if (a === "Uncategorized") return 1;
      if (b === "Uncategorized") return -1;
      return a.localeCompare(b);
    });
  }, [filteredVideos]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showPagination =
    !loading &&
    !error &&
    displayFormat !== "categorywise" &&
    !searchTerm.trim() &&
    total > PAGE_SIZE;
  const activeMediaHint = activeVideo?.storage_link || activeVideoUrl;
  const useNativeAudioPlayer = shouldUseNativeAudioPlayer(activeMediaHint);
  const useNativeVideoPlayer =
    !useNativeAudioPlayer &&
    (shouldUseNativeVideoPlayer(activeMediaHint) ||
      shouldUseNativeVideoPlayer(activeVideoUrl));

  useEffect(() => {
    return () => {
      if (activeVideoUrl) {
        URL.revokeObjectURL(activeVideoUrl);
      }
    };
  }, [activeVideoUrl]);

  const closeVideoDialog = () => {
    setActiveVideo(null);
    setVideoPlayerError(null);
    if (activeVideoUrl) {
      URL.revokeObjectURL(activeVideoUrl);
      setActiveVideoUrl("");
    }
  };

  const handleWatchVideo = async (video: TrainingVideo) => {
    if (!video.storage_link) return;

    setVideoLoadingId(video.id);
    setVideoPlayerError(null);
    setActiveVideo(video);

    try {
      if (activeVideoUrl) {
        URL.revokeObjectURL(activeVideoUrl);
        setActiveVideoUrl("");
      }

      if (isServerHostedVideo(video.storage_link)) {
        const playbackUrl = await getTrainingVideoPlaybackUrl(
          video.storage_link,
        );
        setActiveVideoUrl(playbackUrl);
      } else {
        setActiveVideoUrl(resolveTrainingVideoUrl(video.storage_link));
      }
    } catch (err: any) {
      setVideoPlayerError(err?.message || "Unable to load video");
    } finally {
      setVideoLoadingId(null);
    }
  };

  const renderVideoCard = (video: TrainingVideo, showCategoryChip = true) => (
    <VideoCard key={video.id}>
      <CardContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            mb: 1.5,
          }}
        >
          <VideoTitle>{video.name}</VideoTitle>
          {showCategoryChip && video.category ? (
            <Chip
              label={video.category}
              size="small"
              sx={{
                backgroundColor: "#e6fafc",
                color: "#065b70",
                fontFamily: "Rethink Sans, sans-serif",
              }}
            />
          ) : null}
        </Box>

        <VideoDescription>
          {video.description || "No description provided."}
        </VideoDescription>

        {getVideoDateMeta(video) ? (
          <Box sx={{ mb: 2 }}>
            <MetaText>{getVideoDateMeta(video)}</MetaText>
          </Box>
        ) : null}

        <Button
          variant="contained"
          disabled={!video.storage_link}
          onClick={() => handleWatchVideo(video)}
          sx={{
            textTransform: "none",
            borderRadius: "999px",
            background: video.storage_link
              ? "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)"
              : undefined,
            boxShadow: video.storage_link
              ? "0px 4px 18px rgba(10,130,157,0.18)"
              : undefined,
          }}
        >
          {videoLoadingId === video.id ? "Loading..." : "Watch Video"}
        </Button>
      </CardContent>
    </VideoCard>
  );

  return (
    <PageContainer>
      <Header>
        <TitleBand
          style={{
            height: PAGE_TITLE_BAND_HEIGHT_PX,
            minHeight: PAGE_TITLE_BAND_HEIGHT_PX,
            padding: "0 64px 24px",
            display: "flex",
            alignItems: "flex-end",
            boxSizing: "border-box",
          }}
        >
          <HeaderTitleRow>
            <HeaderIconWrap>
              <TrainingIcon />
            </HeaderIconWrap>
            <HeaderTitle>Learning Center</HeaderTitle>
          </HeaderTitleRow>
        </TitleBand>
        <HeaderSubtitleRow>
          <HeaderSubtitle>
            Browse training and help videos
          </HeaderSubtitle>
        </HeaderSubtitleRow>
      </Header>

      <Content>
        <FilterRow>
          <TextField
            fullWidth
            placeholder="Search by video name or description"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            size="small"
          />

          <TextField
            select
            label="Category"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            size="small"
          >
            <MenuItem value="all">All categories</MenuItem>
            {availableCategories.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Format"
            value={displayFormat}
            onChange={(event) => setDisplayFormat(event.target.value)}
            size="small"
          >
            <MenuItem value="grid">Default</MenuItem>
            <MenuItem value="categorywise">Categorywise</MenuItem>
          </TextField>
        </FilterRow>

        {error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchVideos}>
                Retry
              </Button>
            }
          >
            Unable to load training videos
          </Alert>
        ) : null}

        {loading ? (
          <LoadingCards />
        ) : filteredVideos.length === 0 ? (
          <EmptyState>
            <Typography
              sx={{
                fontFamily: "Rethink Sans, sans-serif",
                fontSize: "20px",
                fontWeight: 600,
                color: "#262626",
              }}
            >
              No training videos available
            </Typography>
            <Typography
              sx={{
                mt: 1,
                fontFamily: "Rethink Sans, sans-serif",
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              Try adjusting your filters or check back later.
            </Typography>
          </EmptyState>
        ) : displayFormat === "categorywise" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {groupedVideos.map(([groupName, groupItems]) => (
              <CategorySection key={groupName}>
                <CategorySectionTitle>{groupName}</CategorySectionTitle>
                <CardsGrid>
                  {groupItems.map((video) => renderVideoCard(video, false))}
                </CardsGrid>
              </CategorySection>
            ))}
          </Box>
        ) : (
          <CardsGrid>
            {filteredVideos.map((video) => renderVideoCard(video))}
          </CardsGrid>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <CircularProgress size={24} />
          </Box>
        ) : null}

        {showPagination ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
            />
          </Box>
        ) : null}
      </Content>

      <Dialog
        open={!!activeVideo}
        onClose={closeVideoDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle
          sx={{
            fontFamily: "Rethink Sans, sans-serif",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box component="span">{activeVideo?.name || "Training Video"}</Box>
          <IconButton
            aria-label="Close video dialog"
            onClick={closeVideoDialog}
            size="small"
            sx={{ color: "#4b5563" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          {videoPlayerError ? (
            <Box
              sx={{
                border: "1px dashed #99d4e1",
                borderRadius: "12px",
                padding: "24px",
                textAlign: "center",
              }}
            >
              <Alert severity="error" sx={{ mb: 2 }}>
                {videoPlayerError}
              </Alert>
              {activeVideoUrl ? (
                <Button
                  component="a"
                  href={activeVideoUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  sx={{
                    textTransform: "none",
                    borderRadius: "999px",
                    background:
                      "linear-gradient(90deg, #0a829d 0%, #35d7e4 100%)",
                  }}
                >
                  Open Video
                </Button>
              ) : null}
            </Box>
          ) : activeVideoUrl ? (
            useNativeAudioPlayer ? (
              <Box
                sx={{
                  width: "100%",
                  borderRadius: "12px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "#f9fafb",
                  p: 2,
                }}
              >
                <Box
                  component="audio"
                  src={activeVideoUrl}
                  controls
                  autoPlay
                  controlsList="nodownload"
                  sx={{ width: "100%", display: "block" }}
                  onError={() =>
                    setVideoPlayerError(
                      "Unable to play this audio from the provided URL",
                    )
                  }
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  aspectRatio: "16 / 9",
                  borderRadius: "12px",
                  backgroundColor: "#000",
                  overflow: "hidden",
                }}
              >
                {useNativeVideoPlayer ? (
                  <Box
                    component="video"
                    src={activeVideoUrl}
                    controls
                    autoPlay
                    playsInline
                    controlsList="nodownload"
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      backgroundColor: "#000",
                    }}
                    onError={() =>
                      setVideoPlayerError(
                        "Unable to play this video from the provided URL",
                      )
                    }
                  />
                ) : (
                  <ReactPlayer
                    src={activeVideoUrl}
                    controls
                    playing
                    width="100%"
                    height="100%"
                    onError={() =>
                      setVideoPlayerError(
                        "Unable to play this video from the provided URL",
                      )
                    }
                  />
                )}
              </Box>
            )
          ) : (
            <Typography
              sx={{
                fontFamily: "Rethink Sans, sans-serif",
                color: "#4b5563",
              }}
            >
              Loading video...
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default TrainingVideosPage;
